import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    // Delay in ms before refreshing, allows the browser to fully regain focus
    refreshDelay: { type: Number, default: 300 },
  };

  connect() {
    // Simplified behavior: if the controller is present, refresh on window focus,
    // but guard to prevent infinite reload loops.
    this.boundHandleFocus = this.handleFocus.bind(this);
    window.addEventListener("focus", this.boundHandleFocus);

    // Handle BFCache restores and tab visibility becoming active
    this.boundHandlePageShow = this.handlePageShow.bind(this);
    window.addEventListener("pageshow", this.boundHandlePageShow);

    // Visibility change: used with reload tracking to avoid loops
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener("visibilitychange", this.boundHandleVisibilityChange);

    // Track active reloads to avoid clearing the guard during a reload
    this.boundHandleBeforeUnload = this.handleBeforeUnload.bind(this);
    window.addEventListener("beforeunload", this.boundHandleBeforeUnload);

    // If we previously marked a reload, clear the flag now that we've loaded
    this.clearReloadingFlag();
  }

  disconnect() {
    if (this.boundHandleFocus) {
      window.removeEventListener("focus", this.boundHandleFocus);
    }
    if (this.boundHandlePageShow) {
      window.removeEventListener("pageshow", this.boundHandlePageShow);
    }
    if (this.boundHandleVisibilityChange) {
      document.removeEventListener("visibilitychange", this.boundHandleVisibilityChange);
    }
    if (this.boundHandleBeforeUnload) {
      window.removeEventListener("beforeunload", this.boundHandleBeforeUnload);
    }
  }

  handleFocus() {
    if (this.shouldRefresh) {
      this.refreshPage();
    }
  }

  handlePageShow(event) {
    // When a page is restored from BFCache or shown after backgrounding, ensure refresh
    // Some browsers set event.persisted when using BFCache; we still refresh to guarantee state
    if (this.shouldRefresh) {
      this.refreshPage();
    }
  }

  handleVisibilityChange() {
    if (document.visibilityState === "hidden") {
      // Do not clear the guard if we're in the middle of a reload
      if (!this.isReloading()) {
        this.clearRefreshGuard();
      }
    }
  }

  refreshPage() {
    // Mark that we've requested a refresh to avoid loops on focus/pageshow after reload
    this.markRefreshed();
    // Mark that a reload is happening so hidden/pagehide doesn't clear our guard
    this.markReloading();
    setTimeout(() => {
      window.location.reload();
    }, this.refreshDelayValue);
  }

  // --- Refresh guard helpers ---
  get storageKey() {
    // Scope to the current path to avoid cross-page interference
    return `cr-refresh-on-focus:${location.pathname}`;
  }

  get shouldRefresh() {
    return !sessionStorage.getItem(this.storageKey);
  }

  markRefreshed() {
    sessionStorage.setItem(this.storageKey, String(Date.now()));
  }

  clearRefreshGuard() {
    sessionStorage.removeItem(this.storageKey);
  }

  // --- Reload tracking helpers ---
  get reloadingKey() {
    return `cr-refresh-on-focus:reloading:${location.pathname}`;
  }

  isReloading() {
    return !!sessionStorage.getItem(this.reloadingKey);
  }

  markReloading() {
    sessionStorage.setItem(this.reloadingKey, "true");
  }

  clearReloadingFlag() {
    sessionStorage.removeItem(this.reloadingKey);
  }

  handleBeforeUnload() {
    this.markReloading();
  }
}
